/**
 * The backup log (decision 72; part 24e log, tables W, R, K, M, principles 2, 7, 8): what this device knows of each
 * book's backups (the files it wrote, in the order it wrote them, the last backup's time and digest, a write under way)
 * and when it last ran. Records are read field by field at the boundary (principle 2, O5): a record that can't be read
 * is no record, so it never names a file to delete. Pure.
 */
import { isId } from '@cockatrice/storage';
import { fileName } from '../export/controller.ts';
import { isLockId } from './lock.ts';
import type { BackupEnding } from './lock.ts';

/** K1: how many of a book's backups are kept: the last this many written. */
export const KEPT = 14;

/** M2, M3: how many days of changes not backed up bring the reminder. */
export const REMIND_DAYS = 7;

/** W2: the most names tried for one backup (`… 2.zip` to `… 99.zip` after the first). */
export const MAX_NAMES = 99;

/** O5: the most files a record holds; a longer list is no record (the app writes at most {@link KEPT} plus one). */
export const MAX_FILES = 1000;

/** The longest file name a record holds (the common file systems' limit, in UTF-16 units here). */
export const MAX_NAME = 255;

const DAY_MS = 86_400_000;

/**
 * A file the app wrote, as it left it (principle 2), the digest of the bundle it holds (S2′: the plaintext bundle's, locked
 * or not), and the lock it was sealed under (part 41b, E2, E4–E6).
 */
export interface BackupFile {
  readonly name: string;
  readonly size: number;
  /** The file's last-modified time as the folder reported it after the write. */
  readonly modified: number;
  readonly sha256: string;
  /** E2, E4–E6: the ID of the backup lock the file was sealed under; null for a plaintext `.zip` (E1, and records before part 41b). */
  readonly lock: string | null;
}

/** How a backup was made: written to the folder, downloaded or shared from the reminder, or a bundle exported by hand (T8). */
export type BackupWay = 'folder' | 'download' | 'export';

/** The last backup of a book: when its bundle was made, its bytes' SHA-256 (hex), and how (principle 7). */
export interface LastBackup {
  readonly at: number;
  readonly sha256: string;
  readonly way: BackupWay;
  /** Part 41b, deep-fix r2 (table D): the lock the backup was sealed under (its ID), null for a plain one. */
  readonly lock: string | null;
}

/**
 * W3: a write under way: the name, the file's size (the sealed file's when locked), the plaintext bundle's digest, when
 * the bundle was made, and the lock it was sealed under (null: plaintext; part 41b, E2).
 */
export interface PendingWrite {
  readonly name: string;
  readonly size: number;
  readonly sha256: string;
  readonly at: number;
  readonly lock: string | null;
}

/** One book's backups on this device. */
export interface BookBackups {
  readonly bookId: string;
  /** M1: when the log first saw the book. */
  readonly seen: number;
  readonly last: LastBackup | null;
  /** The files the app wrote, oldest written first (principle 8). */
  readonly files: readonly BackupFile[];
  readonly pending: PendingWrite | null;
}

/** When a timer's run last ran (T, a local day), and the day the reminder was put off until (M8). */
export interface BackupMeta {
  readonly ran: string | null;
  readonly later: string | null;
}

/** Nothing ran yet, nothing put off. */
export const NO_META: BackupMeta = Object.freeze({ ran: null, later: null });

const WAYS: readonly BackupWay[] = ['folder', 'download', 'export'];
const SHA256 = /^[0-9a-f]{64}$/u;
const DAY = /^\d{4}-\d{2}-\d{2}$/u;

/** The own fields of a plain object with exactly `keys`, and `optional` if it has it, or null. */
function fieldsOf(value: unknown, keys: readonly string[], optional?: string): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  const size = optional !== undefined && fields.has(optional) ? keys.length + 1 : keys.length;
  return fields.size === size && keys.every((key) => fields.has(key)) ? fields : null;
}

/**
 * Part 41b, E2: a file's or a write's lock ID: a record written before part 41b has no `lock` field and reads as
 * plaintext (null); a field present must be null or a lock ID (undefined: no record).
 */
function readLockField(fields: ReadonlyMap<string, unknown> | null): string | null | undefined {
  const lock = fields?.get('lock') ?? null;
  return lock === null || isLockId(lock) ? lock : undefined;
}

/** A time or a size: a whole number from 0. */
function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** A name a record may hold: not empty, not too long, no path. */
function isName(value: unknown): value is string {
  return typeof value === 'string' && value !== '' && value.length <= MAX_NAME && !/[\\/]/u.test(value);
}

/** Whether `value` is a SHA-256 in lowercase hex. */
export function isSha256(value: unknown): value is string {
  return typeof value === 'string' && SHA256.test(value);
}

function readFile(value: unknown): BackupFile | null {
  const fields = fieldsOf(value, ['name', 'size', 'modified', 'sha256'], 'lock');
  const name = fields?.get('name');
  const size = fields?.get('size');
  const modified = fields?.get('modified');
  const sha256 = fields?.get('sha256');
  const lock = readLockField(fields);
  return isName(name) && isCount(size) && isCount(modified) && isSha256(sha256) && lock !== undefined ? { name, size, modified, sha256, lock } : null;
}

function readLast(value: unknown): LastBackup | null | undefined {
  if (value === null) return null;
  const fields = fieldsOf(value, ['at', 'sha256', 'way'], 'lock');
  const at = fields?.get('at');
  const sha256 = fields?.get('sha256');
  const way = WAYS.find((known) => known === fields?.get('way'));
  // D6: a record from before part 41b has no lock: a plain backup.
  const lock = readLockField(fields);
  return isCount(at) && isSha256(sha256) && way !== undefined && lock !== undefined ? { at, sha256, way, lock } : undefined;
}

function readPending(value: unknown): PendingWrite | null | undefined {
  if (value === null) return null;
  const fields = fieldsOf(value, ['name', 'size', 'sha256', 'at'], 'lock');
  const name = fields?.get('name');
  const size = fields?.get('size');
  const sha256 = fields?.get('sha256');
  const at = fields?.get('at');
  const lock = readLockField(fields);
  return isName(name) && isCount(size) && isSha256(sha256) && isCount(at) && lock !== undefined ? { name, size, sha256, at, lock } : undefined;
}

/** O5: the book record `value` is, stored under `key`, or null for anything else (principle 2: it names no file). */
export function readBookBackups(key: unknown, value: unknown): BookBackups | null {
  const fields = fieldsOf(value, ['bookId', 'seen', 'last', 'files', 'pending']);
  const bookId = fields?.get('bookId');
  const seen = fields?.get('seen');
  const last = readLast(fields?.get('last'));
  const pending = readPending(fields?.get('pending'));
  const list = fields?.get('files');
  if (!isId(bookId) || bookId !== key || !isCount(seen) || last === undefined || pending === undefined || !Array.isArray(list) || list.length > MAX_FILES) return null;
  const files: BackupFile[] = [];
  for (const each of list) {
    const file = readFile(each);
    if (file === null) return null;
    files.push(file);
  }
  return { bookId, seen, last, files, pending };
}

/** The meta record `value` is; a field it can't read is its default. */
export function readMeta(value: unknown): BackupMeta {
  const fields = fieldsOf(value, ['ran', 'later']);
  const day = (key: string): string | null => {
    const got = fields?.get(key);
    return typeof got === 'string' && DAY.test(got) ? got : null;
  };
  return { ran: day('ran'), later: day('later') };
}

/** M1: a book the log sees for the first time at `now`. */
export function newBookBackups(bookId: string, now: number): BookBackups {
  return { bookId, seen: now, last: null, files: [], pending: null };
}

/**
 * W1″ (deep-fix r4), Chromium's rule for a name in a folder (`base::i18n::IsFilenameLegal`, which the File System Access
 * API applies outside the origin's private file system): none of `"` `~` `*` `/` `:` `<` `>` `?` `\\` `|`, no control
 * or format character, noncharacter (U+FDD0–FDEF, U+xFFFE, U+xFFFF) or lone surrogate anywhere; no white space, `.` or
 * `~` at either end. `fileName` already drops the separators, controls and formats; this drops the rest, writing `~` as
 * `-`.
 */
const REFUSED_START = /^[\s.~]+/u;

/** W1″: what Chromium refuses anywhere that `fileName` keeps: noncharacters and lone surrogates (`~` is written `-`). */
const REFUSED_ANYWHERE = /[\p{Noncharacter_Code_Point}\p{Cs}]/gu;

/**
 * W1, W1″, W2, E1, E2: the `n`th name tried (from 1) for a backup of book `bookId`, titled `title`, made on `day`, ending
 * `ending` (`.zip` for plaintext, `LOCKED_ENDING` for a locked file): the title as the export cleans it (at most
 * `MAX_FILE_NAME` units, so the day's tail and either ending always fit within {@link MAX_NAME}), then by W1″ (its end is
 * always the day's tail, so only its start needs trimming).
 */
export function backupName(title: string, day: string, bookId: string, n: number, ending: BackupEnding): string {
  const tail = ` ${day} ${bookId.slice(0, 6)}${n === 1 ? '' : ` ${String(n)}`}`;
  const cleaned = fileName(title, 'zip').slice(0, -'.zip'.length).replaceAll('~', '-').replace(REFUSED_ANYWHERE, '').replace(REFUSED_START, '');
  return `${(cleaned === '' ? 'book' : cleaned).trimEnd()}${tail}${ending}`;
}

/** W5, R3: the record once `pending` is written as `found`: the file added last, the last backup set, nothing pending. */
export function afterWrite(record: BookBackups, pending: PendingWrite, found: { readonly size: number; readonly modified: number }): BookBackups {
  const file: BackupFile = { name: pending.name, size: found.size, modified: found.modified, sha256: pending.sha256, lock: pending.lock };
  return { ...record, last: { at: pending.at, sha256: pending.sha256, way: 'folder', lock: pending.lock }, files: [...record.files, file], pending: null };
}

/** K1: the files kept (the last {@link KEPT} written) and the older ones to check (principle 8: written order). */
export function pruning(files: readonly BackupFile[]): { readonly kept: readonly BackupFile[]; readonly older: readonly BackupFile[] } {
  const cut = Math.max(0, files.length - KEPT);
  return { kept: files.slice(cut), older: files.slice(0, cut) };
}

/** K2: whether a file found in the folder is the one the log recorded. */
export function asRecorded(file: BackupFile, found: { readonly size: number; readonly modified: number }): boolean {
  return found.size === file.size && found.modified === file.modified;
}

/** M1–M3: whether the reminder's time has come for `record` at `now` (its digest is checked after). */
export function reminderTime(record: BookBackups, now: number): boolean {
  return now - (record.last?.at ?? record.seen) >= REMIND_DAYS * DAY_MS;
}

/**
 * Part 41b, deep-fix r2, table D: whether the last backup was made under a lock other than `lock` (the one set now, null
 * for none) by the folder or the reminder (D3, D4): due at once, as changed, so a writer who changed or turned off a
 * password they forgot gets a backup they can open, without a folder too. A bundle exported by hand counts as it is
 * (D5, E9); no backup, or one under the same lock, keeps the rules before (D1, D2).
 */
export function lockMoved(record: BookBackups | null, lock: string | null): boolean {
  const last = record?.last ?? null;
  return last !== null && last.way !== 'export' && last.lock !== lock;
}

/** M3, M4, S2, S3: whether a bundle of digest `sha256` holds changes the last backup doesn't. */
export function changed(record: BookBackups | null, sha256: string): boolean {
  return record?.last?.sha256 !== sha256;
}

/** Principle 8: whole local days from the day of `at` to the day of `now`, `zone` minutes ahead of UTC; never below 0. */
export function daysSince(at: number, now: number, zone: number): number {
  const day = (time: number): number => Math.floor((time + zone * 60_000) / DAY_MS);
  return Math.max(0, day(now) - day(at));
}
